// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * Deciding which frames to look at — the decision that sets the whole cost.
 *
 * Embedding is the expensive step (tens to hundreds of milliseconds a frame),
 * so the number of frames sampled IS the runtime. Decoding every frame of a
 * 30 fps video and throwing 29 of every 30 away is the obvious waste, and it is
 * what a naive implementation does.
 *
 * Keyframes are the way out. A keyframe decodes on its own, without walking the
 * frames before it, and dashcam and doorbell recorders use short GOPs — 1 to 2
 * seconds is typical — so the keyframes alone land close to the target rate for
 * free. mediabunny can enumerate them from the container index with
 * `metadataOnly`, without decoding anything at all.
 *
 * But keyframe spacing is a property of the encoder, not of us, and it varies
 * from 0.5 s (a phone) to 10 s (a long-GOP screen recording). So the ladder is
 * built in two directions: thin the keyframes out when they are denser than
 * asked for, and interpolate extra timestamps between them when they are
 * sparser. Both cases are ordinary and neither is an error.
 */

/** Sampling rates offered in the UI, in frames per second of video. */
export const RATES = [0.25, 0.5, 1, 2] as const;
export type Rate = (typeof RATES)[number];

export interface Ladder {
  /** Strictly increasing timestamps in seconds. */
  readonly times: number[];
  /** How many of them fall exactly on a key packet (free to decode). */
  readonly onKeyframe: number;
  /** Median spacing between the source keyframes, or null if there were none. */
  readonly keyframeSpacing: number | null;
}

/**
 * Build the list of timestamps to decode.
 *
 * @param keyframes Key packet timestamps in seconds, ascending. May be empty.
 * @param duration  Video duration in seconds.
 * @param rate      Target samples per second of video.
 */
export function buildLadder(
  keyframes: readonly number[],
  duration: number,
  rate: number,
): Ladder {
  if (!(duration > 0) || !(rate > 0)) return { times: [], onKeyframe: 0, keyframeSpacing: null };

  const step = 1 / rate;
  const keys = dedupeAscending(keyframes.filter((t) => Number.isFinite(t) && t >= 0 && t <= duration));
  const spacing = medianSpacing(keys);

  // No usable key packet list — some containers simply do not carry one. Fall
  // back to an even ladder; every frame then costs a short decode walk, which
  // is slower but correct, and the UI says which path was taken.
  if (keys.length < 2) {
    const times: number[] = [];
    for (let t = 0; t < duration; t += step) times.push(round(t));
    if (times.length === 0) times.push(0);
    return { times, onKeyframe: keys.length === 1 && keys[0] === 0 ? 1 : 0, keyframeSpacing: spacing };
  }

  const times: number[] = [];
  const keySet = new Set<number>();

  // Walk the keyframes, keeping one whenever we have moved at least `step`
  // since the last kept sample. This is the thinning direction.
  let last = -Infinity;
  for (const t of keys) {
    if (t - last >= step - 1e-6) {
      times.push(t);
      keySet.add(t);
      last = t;
    }
  }

  // Now the interpolation direction: wherever two kept samples are further
  // apart than the target step, fill the gap with evenly spaced timestamps.
  // These are not keyframes, so they cost a decode walk — which is the honest
  // price of a long-GOP file and is reported rather than hidden.
  const filled: number[] = [];
  for (let i = 0; i < times.length; i++) {
    const a = times[i]!;
    filled.push(a);
    const b = i + 1 < times.length ? times[i + 1]! : duration;
    const gap = b - a;
    if (gap > step * 1.5) {
      const n = Math.floor(gap / step) - 1;
      for (let k = 1; k <= n; k++) {
        const t = round(a + (gap * k) / (n + 1));
        if (t < b - 1e-6) filled.push(t);
      }
    }
  }

  const out = dedupeAscending(filled);
  return {
    times: out,
    onKeyframe: out.reduce((n, t) => n + (keySet.has(t) ? 1 : 0), 0),
    keyframeSpacing: spacing,
  };
}

/**
 * Wall-clock estimate for a scan, from a throughput measured on this machine.
 *
 * Deliberately takes a measured rate rather than a constant: the same file is
 * minutes on a WebGPU laptop and a long wait on single-threaded WebAssembly,
 * and quoting a model card's number to someone on the slow tier is how a tool
 * loses trust in its first minute.
 */
export function estimateSeconds(frames: number, framesPerSecond: number): number | null {
  if (!(framesPerSecond > 0) || !Number.isFinite(framesPerSecond)) return null;
  return frames / framesPerSecond;
}

export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '—';
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, '0')}s`;
  return `${sec}s`;
}

/** `hh:mm:ss.mmm`, the form WebVTT wants and a person can still read. */
export function timecode(seconds: number): string {
  const clamped = Math.max(0, seconds);
  const h = Math.floor(clamped / 3600);
  const m = Math.floor((clamped % 3600) / 60);
  const s = Math.floor(clamped % 60);
  const ms = Math.round((clamped - Math.floor(clamped)) * 1000);
  const pad = (n: number, w = 2) => String(n).padStart(w, '0');
  return `${pad(h)}:${pad(m)}:${pad(ms === 1000 ? s + 1 : s)}.${pad(ms === 1000 ? 0 : ms, 3)}`;
}

function dedupeAscending(values: readonly number[]): number[] {
  const sorted = [...values].sort((a, b) => a - b);
  const out: number[] = [];
  for (const v of sorted) {
    const prev = out[out.length - 1];
    if (prev === undefined || v - prev > 1e-6) out.push(v);
  }
  return out;
}

function medianSpacing(keys: readonly number[]): number | null {
  if (keys.length < 2) return null;
  const gaps: number[] = [];
  for (let i = 1; i < keys.length; i++) gaps.push(keys[i]! - keys[i - 1]!);
  gaps.sort((a, b) => a - b);
  return round(gaps[gaps.length >> 1]!);
}

const round = (t: number): number => Math.round(t * 1000) / 1000;
